import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { isoDate } from "@/lib/date";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, MapPin, BookOpen, Copy, Check, Download } from "lucide-react";
import { toast } from "sonner";
import { downloadXlsx } from "@/lib/csv";

export default function Adresses() {
  const [query, setQuery] = useState("");
  const [gouv, setGouv] = useState("ALL");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  const search = async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get("/postal/search", { params: { q } });
      setResults(data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  // Initial load (empty query returns top 20)
  useEffect(() => { search(""); /* eslint-disable-next-line */ }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => search(query), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [query]);

  const gouvList = useMemo(() => {
    const set = new Set(results.map((r) => r.gouvernorat).filter(Boolean));
    return Array.from(set).sort();
  }, [results]);

  const filtered = useMemo(() => {
    if (gouv === "ALL") return results;
    return results.filter((r) => r.gouvernorat === gouv);
  }, [results, gouv]);

  const copy = async (r) => {
    const locality = r.localite ? `${r.localite} · ` : "";
    const txt = `${r.code_postal} ${locality}${r.delegation} · ${r.gouvernorat}`;
    try {
      await navigator.clipboard.writeText(txt);
      const key = `${r.code_postal}-${r.delegation}-${r.localite}`;
      setCopiedKey(key);
      toast.success("Adresse copiée");
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const exportExcel = () => {
    if (!filtered.length) {
      toast.error("Aucune adresse à exporter");
      return;
    }
    const today = isoDate();
    const suffix = gouv !== "ALL" ? `-${gouv}` : (query ? `-${query.slice(0, 20)}` : "");
    downloadXlsx(
      `adresses-tunisie${suffix}-${today}`,
      [
        { key: "code_postal", label: "Code postal" },
        { key: "localite", label: "Localité" },
        { key: "delegation", label: "Délégation" },
        { key: "gouvernorat", label: "Gouvernorat" },
      ],
      filtered,
      "Adresses Tunisie",
    );
    toast.success(`${filtered.length} adresse${filtered.length > 1 ? "s" : ""} exportée${filtered.length > 1 ? "s" : ""}`);
  };

  return (
    <div className="space-y-6" data-testid="adresses-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground flex items-center gap-1.5">
            <BookOpen size={12} /> Carnet d'adresses
          </div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl">
            Adresses <span className="text-klein">·</span> Tunisie
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Recherche rapide par code postal, délégation ou gouvernorat.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={exportExcel}
            data-testid="adresses-export-excel"
            className="rounded-sm h-11"
          >
            <Download size={14} className="mr-1.5" /> Excel
          </Button>
          <div className="text-right">
            <div className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground">
              Résultats
            </div>
            <div className="font-display font-black tracking-tighter text-4xl tabular-nums" data-testid="adresses-count">
              {filtered.length}
            </div>
          </div>
        </div>
      </div>

      {/* Search bar */}
      <Card className="rounded-sm border-border p-4">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              data-testid="adresses-search"
              type="search"
              name="address-search"
              autoComplete="off"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Rechercher…'
              className="pl-9 rounded-sm h-11"
            />
          </div>
          <Select value={gouv} onValueChange={setGouv}>
            <SelectTrigger className="rounded-sm h-11" data-testid="adresses-gouv-filter">
              <SelectValue placeholder="Tous gouvernorats" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Tous gouvernorats</SelectItem>
              {gouvList.map((g) => (
                <SelectItem key={g} value={g}>{g}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Results table */}
      <Card className="rounded-sm border-border overflow-hidden">
        <div className="hidden md:grid grid-cols-[90px_1.4fr_1.4fr_1fr_60px] px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground border-b bg-slate-50/60">
          <div>Code postal</div>
          <div>Localité</div>
          <div>Délégation</div>
          <div>Gouvernorat</div>
          <div></div>
        </div>

        {loading && (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">Chargement…</div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="adresses-empty">
            Aucune adresse trouvée.
          </div>
        )}

        {!loading && filtered.map((r, i) => {
          const key = `${r.code_postal}-${r.delegation}-${r.localite}-${i}`;
          const isCopied = copiedKey === `${r.code_postal}-${r.delegation}-${r.localite}`;
          return (
            <div
              key={key}
              data-testid="adresse-row"
              className="grid grid-cols-1 md:grid-cols-[90px_1.4fr_1.4fr_1fr_60px] gap-1 md:gap-0 px-4 py-3 border-b last:border-b-0 hover:bg-slate-50/50 transition-colors items-center"
            >
              <div className="font-mono font-bold text-klein text-sm">{r.code_postal}</div>
              <div className="text-sm font-medium">{r.localite || <span className="text-muted-foreground italic text-xs">—</span>}</div>
              <div className="text-sm text-muted-foreground flex items-center gap-1">
                <MapPin size={12} /> {r.delegation}
              </div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">{r.gouvernorat}</div>
              <div className="flex md:justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => copy(r)}
                  data-testid={`adresse-copy-${r.code_postal}-${i}`}
                  className="rounded-sm h-8 px-2"
                  title="Copier"
                >
                  {isCopied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                </Button>
              </div>
            </div>
          );
        })}
      </Card>

      <p className="text-[11px] text-muted-foreground">
        Source : codes postaux de Tunisie (24 gouvernorats · 261 délégations) · {results.length} entrées chargées.
      </p>
    </div>
  );
}
